import React from 'react';
import { format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from 'date-fns';
import ViewToggle from './ViewToggle';

/**
 * CalendarHeader - Navigation and controls for the calendar
 *
 * Features:
 * - Today button
 * - Previous/Next navigation (adapts to current view)
 * - Current date display
 * - View mode toggle (Month/Week/Day)
 * - Display mode toggle (Calendar/List) - desktop only, appears right-aligned
 * - Category filter toggles
 */
const CalendarHeader = ({
  currentDate,
  setCurrentDate,
  viewMode,
  setViewMode,
  displayMode,
  setDisplayMode,
  categoryFilters,
  setCategoryFilters,
  locations = [],
  selectedLocationId,
  setSelectedLocationId
}) => {
  // Navigate to today
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Navigate previous (depends on view mode)
  const handlePrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subDays(currentDate, 1));
    }
  };

  // Navigate next (depends on view mode)
  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  // Toggle category filter
  const toggleCategory = (category) => {
    setCategoryFilters(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Format the header title based on view mode
  const getHeaderTitle = () => {
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else if (viewMode === 'week') {
      return format(currentDate, 'MMMM yyyy');
    } else {
      // Day view: simplified since DayView shows full date with day name
      return format(currentDate, 'MMMM yyyy');
    }
  };

  const viewModes = [
    { key: 'month', label: 'Month' },
    { key: 'week', label: 'Week' },
    { key: 'day', label: 'Day' }
  ];

  const categories = [
    { key: 'external', label: 'External', color: 'blue', description: 'Customer appointments' },
    { key: 'internal', label: 'Internal', color: 'purple', description: 'Team meetings' },
    { key: 'personal', label: 'Personal', color: 'green', description: 'Private tasks' },
    { key: 'schedule', label: 'Shifts', color: 'orange', description: 'Employee work schedules' }
  ];

  return (
    <div className="bg-white rounded-lg shadow mb-4">
      {/* Top Row: Navigation and View Toggle */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
        {/* Left: Today + Navigation */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          <button
            onClick={handleToday}
            className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Today
          </button>

          <div className="flex items-center gap-0.5 sm:gap-1">
            <button
              onClick={handlePrevious}
              className="p-1 sm:p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Previous"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="p-1 sm:p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Next"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <h2 className="text-sm sm:text-lg font-semibold text-gray-900 ml-1 sm:ml-2 truncate">
            {getHeaderTitle()}
          </h2>
        </div>

        {/* Right: View Mode Toggle + Display Mode Toggle (desktop) */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* Day/Week/Month Toggle - hidden in list mode */}
          {displayMode === 'calendar' && (
            <div className="flex items-center gap-0.5 sm:gap-1 bg-gray-100 rounded-lg p-0.5 sm:p-1">
              {viewModes.map((mode) => (
                <button
                  key={mode.key}
                  onClick={() => setViewMode(mode.key)}
                  className={`px-1.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                    viewMode === mode.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="hidden sm:inline">{mode.label}</span>
                  <span className="sm:hidden">{mode.label.charAt(0)}</span>
                </button>
              ))}
            </div>
          )}

          {/* Calendar/List Toggle - desktop only (hidden on mobile, shown above header there) */}
          <ViewToggle
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            className="hidden lg:flex"
          />
        </div>
      </div>

      {/* Bottom Row: Filter Bar - Compact & Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-2 gap-2 sm:gap-0">
        {/* Category Toggles */}
        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto">
          <span className="text-xs text-gray-500 flex-shrink-0 hidden sm:inline">Show:</span>
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => toggleCategory(category.key)}
              className={`flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full transition-colors flex-shrink-0 ${
                categoryFilters[category.key]
                  ? `bg-${category.color}-100 text-${category.color}-700 border border-${category.color}-200`
                  : 'bg-gray-100 text-gray-500 border border-transparent'
              }`}
              title={category.description}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  categoryFilters[category.key]
                    ? `bg-${category.color}-500`
                    : 'bg-gray-400'
                }`}
              ></span>
              {category.label}
              {categoryFilters[category.key] && (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {/* Location Filter (only show if multiple locations exist) */}
        {locations.length > 1 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <label htmlFor="location-filter" className="text-xs text-gray-500 hidden sm:inline">
              Location:
            </label>
            <select
              id="location-filter"
              value={selectedLocationId || ''}
              onChange={(e) => setSelectedLocationId(e.target.value || null)}
              className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent w-full sm:w-auto"
            >
              <option value="">All Locations</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarHeader;
